import { TaskSource } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { TaskService } from '@weave/schema-gen-ts/dist/schemas/task-center/v1/task_service.pb';
import {
  ListTasksRequestV2,
  TaskService as TaskServiceV2,
} from '@weave/schema-gen-ts/dist/schemas/task-center/v2/task_service.pb';
import { MockPersons } from '@frontend/api-person';
import { DemoIdUtils, DemoInterceptorUtils, FeatureMockRouter } from '@frontend/demo-interceptors';
import { MockAPIRouter } from '@frontend/fetch';
import { taskServiceName } from '../../service';
import { BaseMockRouterContext } from './types';

export interface CallIntelTasksMockRouterContext extends BaseMockRouterContext {
  mockPersonsInstance: MockPersons;
}

export const CallIntelTasksMockRouter: FeatureMockRouter<CallIntelTasksMockRouterContext> = {
  addMockRouterEndpoints: (router) => {
    router.schemaEndpoint(TaskService.DeleteTask, (_, params, next) => {
      if (params.id && DemoIdUtils.checkIsDemoId(params.id)) {
        return MockAPIRouter.json({});
      }
      next();
      return null;
    });

    router.schemaEndpoint(TaskService.UpdateTask, (_, params, next) => {
      if (params.id && DemoIdUtils.checkIsDemoId(params.id)) {
        return MockAPIRouter.json({});
      }
      next();
      return null;
    });
  },
  getRequestInterceptors: ({ interceptorIdPrefix, vertical }) => [
    {
      id: `${interceptorIdPrefix}-tasks-v2`,
      interceptor: async (request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: TaskServiceV2.ListTasksV2,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return request;
        }

        const requestBody: ListTasksRequestV2 = await request.clone().json();

        const originalTaskIds = requestBody.filters?.taskId;

        if (originalTaskIds?.length) {
          const hasDemoTaskIds = originalTaskIds.some((taskId) => DemoIdUtils.checkIsDemoId(taskId));

          if (hasDemoTaskIds) {
            const cleanedTaskIds = originalTaskIds.map((taskId) =>
              DemoIdUtils.checkIsDemoId(taskId) ? DemoIdUtils.removeDemoDataFromId(taskId) : taskId
            );

            return new Request(request, {
              body: JSON.stringify({
                ...requestBody,
                filters: {
                  ...requestBody.filters,
                  source: TaskSource.SOURCE_CALL_INTELLIGENCE,
                  taskId: cleanedTaskIds,
                },
                demoMetadata: {
                  callIntelligence: {
                    vertical,
                  },
                },
                isDemo: true,
              }),
            });
          }
        }

        const filterSource = requestBody.filters?.source;

        if (!requestBody.isDemo || filterSource !== TaskSource.SOURCE_CALL_INTELLIGENCE) {
          return request;
        }

        if (requestBody.filters?.sourceId) {
          requestBody.filters.sourceId = requestBody.filters.sourceId.map(DemoIdUtils.removeDemoDataFromId);
        }

        requestBody.demoMetadata = {
          ...requestBody.demoMetadata,
          callIntelligence: {
            ...requestBody.demoMetadata?.callIntelligence,
            vertical,
          },
        };

        return new Request(request, {
          body: JSON.stringify(requestBody),
        });
      },
    },
  ],
  getResponseInterceptors: ({ interceptorIdPrefix }) => [
    {
      id: `${interceptorIdPrefix}-tasks-v2`,
      interceptor: async (response: Response, request: Request) => {
        const isMatching = await DemoInterceptorUtils.isMatchingApiEndpoint({
          schemaMethod: TaskServiceV2.ListTasksV2,
          requestUrl: request.url,
          method: request.method,
        });

        if (!isMatching) {
          return response;
        }

        try {
          const responseData = await response.json();

          const requestBody: ListTasksRequestV2 = await request.clone().json();
          const hasDemoData = requestBody.isDemo || requestBody.demoMetadata;

          if (!hasDemoData) {
            return MockAPIRouter.json(responseData);
          }

          // Transform response data for demo mode , we are marking source as undefined to hide it in the UI
          const modifiedResponseData = {
            ...responseData,
            tasks:
              responseData.tasks?.map((task: any) => ({
                ...task,
                id: `demo-${task.id}`,
                source: undefined,
              })) || [],
          };

          return MockAPIRouter.json(modifiedResponseData);
        } catch (error) {
          console.error('Failed to transform tasks-v2 demo response:', error);
          return response;
        }
      },
    },
  ],
  invalidateQueries: (queryClient) => {
    queryClient.invalidateQueries([taskServiceName, 'ListTasks']);
  },
};
